import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Shield, 
  Users, 
  UserCheck, 
  UserX,
  Image,
  Upload,
  Loader2,
  Check,
  X,
  Trash2,
  Settings,
  Database,
  Crown,
  Award,
  Plus,
  Edit,
  Save,
  Calculator,
  RotateCcw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({ logo_url: "", background_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  
  // Attendance Models state
  const [attendanceModels, setAttendanceModels] = useState([]);
  const [editingModel, setEditingModel] = useState(null);
  const [newModel, setNewModel] = useState({ 
    name: "", 
    total_races: 4, 
    rules: [
      { min_races: 4, bonus_points: 150 },
      { min_races: 3, bonus_points: 100 },
      { min_races: 2, bonus_points: 50 },
      { min_races: 1, bonus_points: 0 }
    ] 
  });
  
  // Points Scale state
  const [pointsScale, setPointsScale] = useState({});
  const [editingScale, setEditingScale] = useState(false);
  const [tempScale, setTempScale] = useState({});

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const checkAdminAccess = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        if (data.role !== 'admin') {
          toast.error('No tienes permisos de administrador');
          navigate('/dashboard');
          return;
        }
        fetchData();
      } else {
        navigate('/');
      }
    } catch (error) {
      navigate('/');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes, settingsRes] = await Promise.all([
        fetch(`${API}/admin/pending-users`, { credentials: 'include', headers: getHeaders() }),
        fetch(`${API}/admin/users`, { credentials: 'include', headers: getHeaders() }),
        fetch(`${API}/admin/global-settings`)
      ]);

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingUsers(data.users || []);
      }
      if (allRes.ok) {
        const data = await allRes.json();
        setAllUsers(data.users || []);
      }
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setGlobalSettings(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (userId, approve, canEditPilots = false) => {
    try {
      const response = await fetch(`${API}/admin/approve-user`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          is_approved: approve,
          can_edit_pilots: canEditPilots
        })
      });

      if (response.ok) {
        toast.success(approve ? 'Usuario aprobado' : 'Usuario rechazado');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al procesar usuario'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;

    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Usuario eliminado');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al eliminar usuario'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const changeUserRole = async (userId, newRole) => {
    const roleName = newRole === 'admin' ? 'administrador' : 'delegado';
    if (!window.confirm(`¿Cambiar rol a ${roleName}?`)) return;

    try {
      const response = await fetch(`${API}/admin/change-role`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role: newRole })
      });

      if (response.ok) {
        toast.success(`Rol cambiado a ${roleName}`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al cambiar rol'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const saveGlobalSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/global-settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(globalSettings)
      });

      if (response.ok) {
        toast.success('Configuración guardada');
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al guardar'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const uploadPilotsDatabase = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/admin/upload-pilots`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(),
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al subir archivo'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Attendance Models functions
  const fetchAttendanceModels = async () => {
    try {
      const response = await fetch(`${API}/attendance-models`, {
        credentials: 'include',
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAttendanceModels(data);
      }
    } catch (error) {
      console.error('Error fetching attendance models:', error);
    }
  };

  const createAttendanceModel = async () => {
    if (!newModel.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/attendance-models`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newModel.name,
          description: `Modelo para ${newModel.total_races} pruebas`,
          rules: newModel.rules
        })
      });

      if (response.ok) {
        toast.success('Modelo creado correctamente');
        // Reset to default 4-race model
        setNewModel({ 
          name: "", 
          total_races: 4, 
          rules: [
            { min_races: 4, bonus_points: 150 },
            { min_races: 3, bonus_points: 100 },
            { min_races: 2, bonus_points: 50 },
            { min_races: 1, bonus_points: 0 }
          ] 
        });
        fetchAttendanceModels();
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al crear modelo'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const deleteAttendanceModel = async (modelId) => {
    if (!window.confirm('¿Estás seguro de eliminar este modelo de asistencia?')) return;

    try {
      const response = await fetch(`${API}/attendance-models/${modelId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Modelo eliminado');
        fetchAttendanceModels();
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al eliminar'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const seedDefaultModel = async () => {
    try {
      const response = await fetch(`${API}/attendance-models/seed-default`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Modelo AECAR estándar creado');
        fetchAttendanceModels();
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || error?.message || 'Error'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  // When total_races changes, regenerate rules array
  const handleTotalRacesChange = (numRaces) => {
    const num = parseInt(numRaces) || 4;
    const newRules = [];
    
    // Generate rules from num down to 1
    // Default points: first gets 150, then decreasing by 50, minimum 0
    for (let i = num; i >= 1; i--) {
      const defaultPoints = Math.max(0, 150 - (num - i) * 50);
      newRules.push({ min_races: i, bonus_points: defaultPoints });
    }
    
    setNewModel(prev => ({
      ...prev,
      total_races: num,
      rules: newRules
    }));
  };

  const updateNewModelRule = (index, value) => {
    setNewModel(prev => ({
      ...prev,
      rules: prev.rules.map((r, i) => i === index ? { ...r, bonus_points: parseInt(value) || 0 } : r)
    }));
  };

  // Points Scale functions
  const fetchPointsScale = async () => {
    try {
      const response = await fetch(`${API}/points-scale`, {
        credentials: 'include',
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPointsScale(data.scale || {});
        setTempScale(data.scale || {});
      }
    } catch (error) {
      console.error('Error fetching points scale:', error);
    }
  };

  const savePointsScale = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/points-scale`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ scale: tempScale })
      });

      if (response.ok) {
        toast.success('Escala de puntos actualizada');
        setPointsScale(tempScale);
        setEditingScale(false);
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error al guardar'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const resetPointsScale = async () => {
    if (!window.confirm('¿Restaurar la escala de puntos a los valores AECAR por defecto?')) return;
    
    try {
      const response = await fetch(`${API}/points-scale/reset`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Escala restaurada a valores por defecto');
        fetchPointsScale();
        setEditingScale(false);
      } else {
        const error = await response.json();
        toast.error(String(error?.detail || 'Error'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const updateTempScaleValue = (position, value) => {
    setTempScale(prev => ({
      ...prev,
      [position]: parseInt(value) || 0
    }));
  };

  // Load attendance models when tab changes
  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAttendanceModels();
    }
    if (activeTab === 'points') {
      fetchPointsScale();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="font-heading font-black text-2xl uppercase">
              Administración
            </h1>
            <p className="text-muted-foreground text-sm">
              Gestión de usuarios y configuración global
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          <Button
            variant={activeTab === "pending" ? "default" : "ghost"}
            onClick={() => setActiveTab("pending")}
            className="gap-2"
          >
            <UserCheck className="w-4 h-4" />
            Pendientes ({pendingUsers.length})
          </Button>
          <Button
            variant={activeTab === "users" ? "default" : "ghost"}
            onClick={() => setActiveTab("users")}
            className="gap-2"
          >
            <Users className="w-4 h-4" />
            Todos los usuarios
          </Button>
          <Button
            variant={activeTab === "settings" ? "default" : "ghost"}
            onClick={() => setActiveTab("settings")}
            className="gap-2"
          >
            <Settings className="w-4 h-4" />
            Configuración
          </Button>
          <Button
            variant={activeTab === "pilots" ? "default" : "ghost"}
            onClick={() => setActiveTab("pilots")}
            className="gap-2"
          >
            <Database className="w-4 h-4" />
            Base de Pilotos
          </Button>
          <Button
            variant={activeTab === "attendance" ? "default" : "ghost"}
            onClick={() => setActiveTab("attendance")}
            className="gap-2"
          >
            <Award className="w-4 h-4" />
            Modelos Asistencia
          </Button>
          <Button
            variant={activeTab === "points" ? "default" : "ghost"}
            onClick={() => setActiveTab("points")}
            className="gap-2"
          >
            <Calculator className="w-4 h-4" />
            Escala Puntos
          </Button>
        </div>

        {/* Pending Users Tab */}
        {activeTab === "pending" && (
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-primary" />
                Usuarios Pendientes de Aprobación
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No hay usuarios pendientes de aprobación
                </p>
              ) : (
                <div className="space-y-3">
                  {pendingUsers.map((u) => (
                    <div
                      key={u.user_id}
                      className="flex items-center justify-between p-4 bg-black/20 rounded-lg border border-white/5"
                    >
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                        <p className="text-xs text-muted-foreground/60">
                          Registrado: {new Date(u.created_at).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => approveUser(u.user_id, true, false)}
                          className="gap-1"
                        >
                          <Check className="w-4 h-4" />
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => approveUser(u.user_id, true, true)}
                          className="gap-1 border-primary text-primary"
                        >
                          <Check className="w-4 h-4" />
                          Aprobar + Editar Pilotos
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => approveUser(u.user_id, false)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* All Users Tab */}
        {activeTab === "users" && (
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Todos los Usuarios
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {allUsers.map((u) => (
                  <div
                    key={u.user_id}
                    className="flex items-center justify-between p-4 bg-black/20 rounded-lg border border-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {u.name}
                          {u.role === 'admin' && (
                            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                              ADMIN
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                        <div className="flex gap-2 mt-1">
                          {u.is_approved ? (
                            <span className="text-xs text-green-400">✓ Aprobado</span>
                          ) : (
                            <span className="text-xs text-yellow-400">⏳ Pendiente</span>
                          )}
                          {u.can_edit_pilots && (
                            <span className="text-xs text-blue-400">✎ Edita pilotos</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {u.role !== 'admin' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => changeUserRole(u.user_id, 'admin')}
                          title="Hacer administrador"
                          className="gap-1"
                        >
                          <Crown className="w-4 h-4" />
                          Hacer Admin
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => approveUser(u.user_id, u.is_approved, !u.can_edit_pilots)}
                          title={u.can_edit_pilots ? "Quitar permiso de pilotos" : "Dar permiso de pilotos"}
                        >
                          {u.can_edit_pilots ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteUser(u.user_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {u.role === 'admin' && u.user_id !== user?.user_id && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => changeUserRole(u.user_id, 'delegate')}
                          title="Quitar administrador"
                          className="gap-1"
                        >
                          <UserX className="w-4 h-4" />
                          Quitar Admin
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Personalización de Imágenes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Logos Section */}
              <div className="space-y-4">
                <h3 className="font-heading text-sm uppercase text-primary border-b border-primary/30 pb-2">
                  Logos
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Logo Principal (fondo oscuro)</Label>
                    <Input
                      value={globalSettings.logo_url || ""}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, logo_url: e.target.value })}
                      placeholder="https://ejemplo.com/logo.png"
                      className="bg-black/20 border-white/10"
                    />
                    {globalSettings.logo_url && (
                      <div className="bg-black/40 p-4 rounded mt-2">
                        <img src={globalSettings.logo_url} alt="Logo" className="h-16 object-contain" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Logo Claro (fondo claro/emails)</Label>
                    <Input
                      value={globalSettings.logo_light_url || ""}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, logo_light_url: e.target.value })}
                      placeholder="https://ejemplo.com/logo-light.png"
                      className="bg-black/20 border-white/10"
                    />
                    {globalSettings.logo_light_url && (
                      <div className="bg-white/90 p-4 rounded mt-2">
                        <img src={globalSettings.logo_light_url} alt="Logo claro" className="h-16 object-contain" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Landing Page Section */}
              <div className="space-y-4">
                <h3 className="font-heading text-sm uppercase text-primary border-b border-primary/30 pb-2">
                  Página de Inicio / Login
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fondo de la página de acceso</Label>
                    <Input
                      value={globalSettings.landing_background_url || ""}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, landing_background_url: e.target.value })}
                      placeholder="https://ejemplo.com/landing-bg.jpg"
                      className="bg-black/20 border-white/10"
                    />
                    <p className="text-xs text-muted-foreground">Imagen de fondo para login/registro</p>
                    {globalSettings.landing_background_url && (
                      <img src={globalSettings.landing_background_url} alt="Landing bg" className="h-24 w-full object-cover rounded mt-2" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Imagen Hero de la landing</Label>
                    <Input
                      value={globalSettings.landing_hero_image_url || ""}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, landing_hero_image_url: e.target.value })}
                      placeholder="https://ejemplo.com/hero.jpg"
                      className="bg-black/20 border-white/10"
                    />
                    <p className="text-xs text-muted-foreground">Imagen destacada junto al formulario</p>
                    {globalSettings.landing_hero_image_url && (
                      <img src={globalSettings.landing_hero_image_url} alt="Hero" className="h-24 w-full object-cover rounded mt-2" />
                    )}
                  </div>
                </div>
              </div>

              {/* Dashboard Section */}
              <div className="space-y-4">
                <h3 className="font-heading text-sm uppercase text-primary border-b border-primary/30 pb-2">
                  Dashboard y App
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fondo general de la aplicación</Label>
                    <Input
                      value={globalSettings.background_url || ""}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, background_url: e.target.value })}
                      placeholder="https://ejemplo.com/app-bg.jpg"
                      className="bg-black/20 border-white/10"
                    />
                    <p className="text-xs text-muted-foreground">Fondo para todas las páginas internas</p>
                    {globalSettings.background_url && (
                      <img src={globalSettings.background_url} alt="App bg" className="h-24 w-full object-cover rounded mt-2" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Fondo específico del Dashboard</Label>
                    <Input
                      value={globalSettings.dashboard_background_url || ""}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, dashboard_background_url: e.target.value })}
                      placeholder="https://ejemplo.com/dashboard-bg.jpg"
                      className="bg-black/20 border-white/10"
                    />
                    <p className="text-xs text-muted-foreground">Si está vacío, usa el fondo general</p>
                    {globalSettings.dashboard_background_url && (
                      <img src={globalSettings.dashboard_background_url} alt="Dashboard bg" className="h-24 w-full object-cover rounded mt-2" />
                    )}
                  </div>
                </div>
              </div>

              {/* Email Section */}
              <div className="space-y-4">
                <h3 className="font-heading text-sm uppercase text-primary border-b border-primary/30 pb-2">
                  Emails
                </h3>
                <div className="space-y-2">
                  <Label>Imagen de cabecera para emails</Label>
                  <Input
                    value={globalSettings.email_header_image_url || ""}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, email_header_image_url: e.target.value })}
                    placeholder="https://ejemplo.com/email-header.png"
                    className="bg-black/20 border-white/10"
                  />
                  <p className="text-xs text-muted-foreground">Logo o imagen que aparece en los emails de clasificación</p>
                  {globalSettings.email_header_image_url && (
                    <div className="bg-white/10 p-4 rounded mt-2 text-center">
                      <img src={globalSettings.email_header_image_url} alt="Email header" className="h-16 object-contain mx-auto" />
                    </div>
                  )}
                </div>
              </div>

              <Button onClick={saveGlobalSettings} disabled={saving} className="w-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar Toda la Configuración
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Pilots Database Tab */}
        {activeTab === "pilots" && (
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" />
                Base de Datos de Pilotos AECAR
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Sube un archivo Excel o CSV con la base de datos oficial de pilotos AECAR.
                Esta base será utilizada para el autocompletado de pilotos en toda la aplicación.
              </p>
              
              <div className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center">
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  Formato esperado: licencia, nombre, apellidos, email, categoria
                </p>
                <input
                  type="file"
                  id="pilots-upload"
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={uploadPilotsDatabase}
                />
                <Button
                  onClick={() => document.getElementById('pilots-upload').click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Subir Base de Pilotos
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                ⚠️ Al subir un nuevo archivo, se reemplazará toda la base de datos existente.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Attendance Models Tab */}
        {activeTab === "attendance" && (
          <div className="space-y-6">
            {/* Create New Model */}
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Crear Nuevo Modelo de Asistencia
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Step 1: Number of races */}
                <div className="space-y-2">
                  <Label className="text-base">1. Número de Pruebas del Campeonato</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Indica cuántas pruebas tiene el campeonato para el que creas este modelo.
                  </p>
                  <div className="flex gap-2">
                    {[3, 4, 5, 6, 7, 8].map((num) => (
                      <Button
                        key={num}
                        variant={newModel.total_races === num ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleTotalRacesChange(num)}
                        className="w-12"
                      >
                        {num}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Step 2: Model name */}
                <div className="space-y-2">
                  <Label className="text-base">2. Nombre del Modelo</Label>
                  <Input
                    value={newModel.name}
                    onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                    placeholder={`Ej: AECAR ${newModel.total_races} Pruebas`}
                    className="bg-black/20 border-white/10 max-w-md"
                  />
                </div>

                {/* Step 3: Points per races attended */}
                <div className="space-y-3">
                  <Label className="text-base">3. Puntos por Pruebas Disputadas</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Asigna los puntos extra que recibe un piloto según el número de pruebas que dispute.
                    Puedes poner 0 si no quieres dar puntos por esa cantidad.
                  </p>
                  
                  <div className="bg-black/20 rounded-lg p-4 space-y-3">
                    {newModel.rules.map((rule, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-32 text-right">
                          <span className="text-primary font-mono font-bold">{rule.min_races}</span>
                          <span className="text-muted-foreground ml-1">
                            {rule.min_races === 1 ? 'prueba' : 'pruebas'}
                          </span>
                        </div>
                        <span className="text-muted-foreground">=</span>
                        <Input
                          type="number"
                          value={rule.bonus_points}
                          onChange={(e) => updateNewModelRule(index, e.target.value)}
                          className="w-24 bg-black/30 border-white/10 text-center font-mono"
                          min="0"
                          placeholder="0"
                        />
                        <span className="text-muted-foreground">puntos</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-white/10">
                  <Button 
                    onClick={createAttendanceModel} 
                    disabled={saving || !newModel.name.trim()}
                    className="flex-1 md:flex-none"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Crear Modelo "{newModel.name || '...'}"
                  </Button>
                </div>
                
                <div className="text-center pt-2">
                  <p className="text-xs text-muted-foreground mb-2">¿Quieres usar el modelo estándar AECAR de 4 pruebas?</p>
                  <Button variant="outline" size="sm" onClick={seedDefaultModel}>
                    Crear Modelo AECAR Estándar (4 pruebas)
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Existing Models */}
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Modelos de Asistencia Existentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {attendanceModels.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No hay modelos de asistencia creados. Crea el modelo AECAR estándar o crea uno personalizado.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {attendanceModels.map((model) => (
                      <div 
                        key={model.model_id} 
                        className="bg-black/20 rounded-lg p-4 border border-white/5"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-heading text-lg text-primary">
                              {model.name}
                              {model.is_default && (
                                <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                                  ESTÁNDAR
                                </span>
                              )}
                            </h4>
                            {model.description && (
                              <p className="text-sm text-muted-foreground">{model.description}</p>
                            )}
                          </div>
                          {!model.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteAttendanceModel(model.model_id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {model.rules?.map((rule, idx) => (
                            <span 
                              key={idx}
                              className="bg-primary/10 text-primary text-sm px-3 py-1 rounded-full"
                            >
                              {rule.min_races}+ carreras = {rule.bonus_points} pts
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        {/* Points Scale Tab */}
        {activeTab === "points" && (
          <div className="space-y-6" data-testid="points-scale-tab">
            <Card className="bg-card/50 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-primary" />
                    Escala de Puntos AECAR (Art. 17.1)
                  </CardTitle>
                  <div className="flex gap-2">
                    {!editingScale ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setTempScale({...pointsScale}); setEditingScale(true); }}
                        className="gap-1"
                        data-testid="edit-scale-btn"
                      >
                        <Edit className="w-4 h-4" />
                        Editar
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={savePointsScale}
                          disabled={saving}
                          className="gap-1"
                          data-testid="save-scale-btn"
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Guardar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setTempScale({...pointsScale}); setEditingScale(false); }}
                          data-testid="cancel-scale-btn"
                        >
                          <X className="w-4 h-4" />
                          Cancelar
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetPointsScale}
                      className="gap-1 text-yellow-400 border-yellow-400/30 hover:bg-yellow-400/10"
                      data-testid="reset-scale-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Restaurar
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Posiciones 1-90 con puntos asignados. Posiciones 91+ reciben 1 punto.
                </p>
              </CardHeader>
              <CardContent>
                {Object.keys(editingScale ? tempScale : pointsScale).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Cargando escala de puntos...
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2" data-testid="points-scale-grid">
                    {Array.from({ length: 90 }, (_, i) => i + 1).map((pos) => {
                      const currentVal = editingScale ? (tempScale[pos] ?? tempScale[String(pos)] ?? 0) : (pointsScale[pos] ?? pointsScale[String(pos)] ?? 0);
                      const defaultVal = pointsScale[pos] ?? pointsScale[String(pos)] ?? 0;
                      const tempVal = tempScale[pos] ?? tempScale[String(pos)] ?? 0;
                      const isModified = editingScale && tempVal !== defaultVal;

                      return (
                        <div
                          key={pos}
                          className={`flex items-center gap-2 p-2 rounded-lg border ${
                            isModified
                              ? "border-primary/50 bg-primary/10"
                              : "border-white/5 bg-black/20"
                          }`}
                          data-testid={`points-pos-${pos}`}
                        >
                          <span className="text-xs font-mono text-muted-foreground w-8 text-right shrink-0">
                            {pos}º
                          </span>
                          {editingScale ? (
                            <Input
                              type="number"
                              value={tempVal}
                              onChange={(e) => updateTempScaleValue(String(pos), e.target.value)}
                              className="h-8 text-center font-mono text-sm bg-black/30 border-white/10 px-1"
                              min="0"
                              data-testid={`points-input-${pos}`}
                            />
                          ) : (
                            <span className="font-mono text-sm font-bold text-center flex-1">
                              {currentVal}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Footer info */}
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Posiciones 91 en adelante: 1 punto</span>
                  <span>Descalificación: 0 puntos</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
